import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useOrderPolling, isTerminalOrderStatus } from '@/hooks/useOrderPolling';
import { useCart } from '@/store/cart';
import { Badge, Button, Card, CardTitle, Skeleton, SkeletonGroup } from '@/components/ui';
import { QrTicket } from '@/components/QrTicket';
import { formatCOP, formatEventDate } from '@/lib/format';
import { messageForCode } from '@/lib/checkout/messages';

/**
 * CheckoutSuccess — TASK-C08 (PRIORIDAD 5).
 *
 * Reads `?order_id=X` from URL. Polls /order/:id until status is terminal.
 *  - confirmed / qr_issued → display QR(s) + order summary + CTA "Mis boletas"
 *  - used                  → display "Boleta ya utilizada" message
 *  - failed / cancelled    → display error + CTA volver al evento
 *  - 404                   → redirect / con toast
 *
 * On first successful render of a QR, calls `cart.finalizeCheckout()` to
 * clear the local cart + idempotency-key. We do this LATE (not in
 * Checkout.tsx) so the back-button navigation from /checkout/success →
 * /checkout still has lock state visible briefly if needed.
 */
export function CheckoutSuccess(): JSX.Element {
  const [params] = useSearchParams();
  const orderId = params.get('order_id');
  const navigate = useNavigate();
  const cart = useCart();

  const orderPoll = useOrderPolling(orderId ?? undefined, { intervalMs: 2_500 });

  const status = orderPoll.data?.status;
  const hasQrs = (orderPoll.data?.qrCodes?.length ?? 0) > 0;
  const isTerminal = isTerminalOrderStatus(status);

  // Once we see a terminal-with-QR state, finalize the cart (clear local
  // state). Idempotent because finalizeCheckout is safe to call multiple
  // times.
  useEffect(() => {
    if (hasQrs && (status === 'confirmed' || status === 'qr_issued' || status === 'used')) {
      cart.finalizeCheckout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, hasQrs]);

  if (!orderId) {
    return (
      <section className="container-fc py-12">
        <h1 className="font-display text-3xl">Falta el identificador de la orden</h1>
        <Button className="mt-6" onClick={() => navigate('/')}>
          Volver al inicio
        </Button>
      </section>
    );
  }

  // ---- 404 / error states ----
  if (orderPoll.isError) {
    const code = (orderPoll.error as { code?: string } | null)?.code;
    const status404 = (orderPoll.error as { status?: number } | null)?.status === 404;
    return (
      <section className="container-fc py-12">
        <h1 className="font-display text-3xl">
          {status404 ? 'No encontramos esa orden' : 'No pudimos cargar tu compra'}
        </h1>
        <p className="mt-2 text-fc-muted">{messageForCode(code, status404 ? 404 : 500)}</p>
        <Button className="mt-6" variant="secondary" onClick={() => navigate('/')}>
          Volver al inicio
        </Button>
      </section>
    );
  }

  // ---- Loading first response or still polling, no QRs yet ----
  if (!orderPoll.data || (!hasQrs && !isTerminal)) {
    return (
      <section className="container-fc py-12">
        <h1 className="font-display text-3xl">Confirmando tu pago…</h1>
        <p className="mt-2 text-fc-muted" role="status" aria-live="polite">
          Estamos esperando la confirmación del procesador. Esto suele tomar unos segundos.
        </p>
        <div className="mt-8">
          <SkeletonGroup label="Generando boletas">
            <Skeleton shape="h-60 w-60" />
            <Skeleton shape="mt-4 h-5 w-1/2" />
            <Skeleton shape="mt-2 h-4 w-2/3" />
          </SkeletonGroup>
        </div>
      </section>
    );
  }

  // ---- Terminal: failed / cancelled / expired ----
  if (status === 'failed' || status === 'cancelled' || status === 'expired') {
    return (
      <section className="container-fc py-12">
        <h1 className="font-display text-3xl">No pudimos completar la compra</h1>
        <p className="mt-2 text-fc-muted">
          Estado de la orden: <Badge tone="danger">{status}</Badge>
        </p>
        <p className="mt-2 text-fc-muted">{messageForCode('PAYMENT_DECLINED')}</p>
        <div className="mt-6 flex flex-col gap-2 md:flex-row">
          <Button onClick={() => navigate('/')}>Ver eventos</Button>
          <Button variant="secondary" onClick={() => navigate('/my-tickets')}>
            Ver mis boletas
          </Button>
        </div>
      </section>
    );
  }

  // ---- Terminal: used (alert + still show QR) ----
  // ---- Terminal: confirmed / qr_issued — happy path ----
  const order = orderPoll.data;
  return (
    <section className="container-fc py-8 md:py-12">
      <header className="mb-6">
        <h1 className="font-display text-3xl md:text-4xl">¡Pago confirmado!</h1>
        <p className="mt-2 text-fc-muted">
          Order <code className="font-mono text-fc-ink">{order.orderNumber}</code>
        </p>
        {status === 'used' && (
          <Badge tone="warning" className="mt-3">
            Esta boleta ya fue utilizada.
          </Badge>
        )}
      </header>

      <Card variant="default" padded>
        <CardTitle>Tus boletas</CardTitle>
        <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2">
          {order.qrCodes.map((qr, idx) => (
            <div key={qr.id} className="flex flex-col items-center gap-3">
              <QrTicket
                code={qr.code}
                label={
                  qr.seatLabel
                    ? `Asiento ${qr.seatLabel}`
                    : `Boleta ${idx + 1} de ${order.qrCodes.length}`
                }
                size={240}
              />
              {/* F5 — QR nominal: titular del asiento */}
              {qr.holderName && (
                <p className="text-center text-sm font-medium text-fc-ink">{qr.holderName}</p>
              )}
              <p className="text-center text-xs text-fc-muted">
                Vence <time dateTime={qr.expiresAt}>{formatEventDate(qr.expiresAt)}</time>
              </p>
            </div>
          ))}
          {order.qrCodes.length === 0 && (
            <p className="text-fc-muted">El QR se está emitiendo. Refrescá en unos segundos.</p>
          )}
        </div>

        <div className="mt-6 border-t border-fc-line pt-4">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-fc-muted">Resumen</h3>
          <dl className="mt-2 grid grid-cols-1 gap-y-1 text-sm md:grid-cols-2">
            <dt className="text-fc-muted">Total cobrado</dt>
            <dd className="font-semibold tabular-nums">{formatCOP(order.totalCents)}</dd>
            <dt className="text-fc-muted">Items</dt>
            <dd>{order.items.reduce((sum, it) => sum + it.quantity, 0)}</dd>
            {order.confirmedAt && (
              <>
                <dt className="text-fc-muted">Confirmado</dt>
                <dd>{formatEventDate(order.confirmedAt)}</dd>
              </>
            )}
          </dl>
        </div>

        <div className="mt-6 flex flex-col gap-2 md:flex-row md:justify-end">
          <Button variant="ghost" onClick={() => navigate('/')}>
            Ver más eventos
          </Button>
          <Button variant="primary" onClick={() => navigate('/my-tickets')}>
            Ver en Mis boletas
          </Button>
        </div>
      </Card>
    </section>
  );
}
